import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentMethods from '../../components/Pricing/PaymentMethods';

type Period = '1m' | '6m' | '12m';

interface PeriodDetails {
  key: Period;
  tabLabel: string;
  badge?: string;
  planName: string;
  roleBadge: string;
  pricePerMonth: string;
  billingText: string;
  savingsText?: string;
  hasBonus: boolean;
  hasValuationAlerts: boolean;
  hasWatchlistIa: boolean;
  planId: string;
}

const PERIOD_CONFIG: Record<Period, PeriodDetails> = {
  '1m': {
    key: '1m',
    tabLabel: '1 mois',
    badge: 'Sans engagement',
    planName: 'Kanea Starter',
    roleBadge: 'Sans engagement',
    pricePerMonth: '7 900 FCFA',
    billingText: 'Facturé 7 900 FCFA chaque mois · Résiliable à tout moment',
    hasBonus: false,
    hasValuationAlerts: false,
    hasWatchlistIa: false,
    planId: 'kanea_starter',
  },
  '6m': {
    key: '6m',
    tabLabel: '6 mois',
    badge: '⭐ Populaire · -16%',
    planName: 'Kanea Pro',
    roleBadge: 'Le plus populaire',
    pricePerMonth: '6 650 FCFA',
    billingText: 'Facturé 39 900 FCFA tous les 6 mois',
    savingsText: 'Économisez 7 500 FCFA par rapport au mensuel',
    hasBonus: false,
    hasValuationAlerts: true,
    hasWatchlistIa: true,
    planId: 'kanea_pro',
  },
  '12m': {
    key: '12m',
    tabLabel: '12 mois',
    badge: '💎 2 mois offerts · -26%',
    planName: 'Kanea Annuel VIP',
    roleBadge: 'Offre Annuelle VIP',
    pricePerMonth: '5 825 FCFA',
    billingText: 'Facturé 69 900 FCFA par an · 2 mois offerts',
    savingsText: 'Économisez 24 900 FCFA par an (2 mois offerts) + Bonus exclusifs',
    hasBonus: true,
    hasValuationAlerts: true,
    hasWatchlistIa: true,
    planId: 'kanea_illimite',
  },
};

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const LandingPricingSection: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('12m');
  const activePlan = PERIOD_CONFIG[selectedPeriod];

  return (
    <section className="landing-pricing" id="tarifs">
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div className="landing-section-label">Tarifs simples & transparents</div>
        <h2 className="landing-section-title">Investissez avec méthode, sans mauvaise surprise</h2>
        <p className="landing-section-sub">
          Démarrez gratuitement pour vous initier. Choisissez l'abonnement adapté à vos objectifs d'investissement.
        </p>

        {/* ── Durée Toggle pour l'offre Payante ── */}
        <div className="pricing-period-toggle-wrapper">
          <div className="pricing-period-toggle">
            {(Object.keys(PERIOD_CONFIG) as Period[]).map((periodKey) => {
              const item = PERIOD_CONFIG[periodKey];
              const isActive = selectedPeriod === periodKey;
              return (
                <button
                  key={periodKey}
                  type="button"
                  className={`pricing-period-btn ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedPeriod(periodKey)}
                >
                  <span className="period-btn-label">{item.tabLabel}</span>
                  {item.badge && (
                    <span className={`period-btn-badge ${isActive ? 'badge-active' : ''}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Grille 2 Cartes : Gratuit vs Offre Payante Dynamique ── */}
      <div className="pricing-cards-grid">
        
        {/* CARTE 1 : GRATUIT */}
        <div className="pricing-card free-card">
          <div className="pricing-card-header">
            <span className="pricing-card-badge free">Accès Libre</span>
            <h3 className="pricing-card-title">Gratuit</h3>
            <p className="pricing-card-desc">
              Pour découvrir la plateforme, suivre les cotations et s'initier aux analyses de la BRVM.
            </p>
            <div className="pricing-card-price-row">
              <span className="pricing-card-price">0 FCFA</span>
              <span className="pricing-card-period">/ pour toujours</span>
            </div>
            <p className="pricing-card-billing-sub">Aucune carte bancaire requise</p>
          </div>

          <ul className="pricing-card-features">
            <li className="feature-item included">
              <CheckIcon />
              <span>Données de marché en fin de journée (EOD)</span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span>Simulateur de portefeuille virtuel libre</span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span>Nia IA — Analyste conversationnelle (5 msg/jour)</span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span>Alertes de cours par email (5 alertes max)</span>
            </li>
            <li className="feature-item excluded">
              <CrossIcon />
              <span>Indice de Valeur Kanea (IVK™)</span>
            </li>
            <li className="feature-item excluded">
              <CrossIcon />
              <span>Indicateurs techniques complets (RSI, Bollinger, OBV...)</span>
            </li>
            <li className="feature-item excluded">
              <CrossIcon />
              <span>Signaux Pro & Alertes de sous-évaluation</span>
            </li>
            <li className="feature-item excluded">
              <CrossIcon />
              <span>Watchlist IA & Sentiment de marché</span>
            </li>
            <li className="feature-item excluded">
              <CrossIcon />
              <span>Bonus exclusifs & rapports stratégiques</span>
            </li>
          </ul>

          <div className="pricing-card-cta-wrapper">
            <button
              type="button"
              className="pricing-btn-free"
              onClick={() => navigate('/members/signup')}
            >
              Créer un compte gratuit
            </button>
          </div>
        </div>

        {/* CARTE 2 : OFFRE PAYANTE (STARTER / PRO / ILLIMITÉ) */}
        <div className="pricing-card pro-card">
          {activePlan.hasBonus ? (
            <div className="pricing-pro-top-tag">
              ⭐ OFFRE ANNUELLE · 2 MOIS OFFERTS + BONUS EXCLUSIFS
            </div>
          ) : (
            <div className="pricing-pro-top-tag">
              ⚡ FORMULE {activePlan.planName.toUpperCase()}
            </div>
          )}

          <div className="pricing-card-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="pricing-card-title pro">{activePlan.planName}</h3>
              <span className="pricing-card-badge pro">{activePlan.roleBadge}</span>
            </div>
            <p className="pricing-card-desc">
              {activePlan.hasBonus 
                ? "L'expérience investisseur complète : Moteur de valorisation IVK™, Nia IA en illimité et accès aux bonus exclusifs."
                : "Les données fondamentales, les modèles de valorisation et Nia IA pour analyser froidement la BRVM."}
            </p>
            <div className="pricing-card-price-row">
              <span className="pricing-card-price pro">{activePlan.pricePerMonth}</span>
              <span className="pricing-card-period">/ mois</span>
            </div>
            <p className="pricing-card-billing-sub highlight">{activePlan.billingText}</p>
            {activePlan.savingsText && (
              <p className="pricing-card-savings">{activePlan.savingsText}</p>
            )}
          </div>

          <ul className="pricing-card-features">
            <li className="feature-item included strong">
              <CheckIcon />
              <span><strong>Tout ce qui est dans le plan Gratuit</strong></span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span><strong>Nia IA en accès illimité</strong> (graphiques, bilans, calculs instantanés)</span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span><strong>Indice de Valeur Kanea (IVK™)</strong> : Valorisation fondamentale sur les 48 sociétés</span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span><strong>Indicateurs techniques complets</strong> (RSI 14, Bollinger, CCI, OBV, Stochastique)</span>
            </li>
            <li className="feature-item included">
              <CheckIcon />
              <span><strong>Signaux Pro</strong> & alertes de marché</span>
            </li>
            
            {/* Alertes Valorisation */}
            {activePlan.hasValuationAlerts ? (
              <li className="feature-item included">
                <CheckIcon />
                <span><strong>Alertes de valorisation par email</strong> (sous-évaluation détectée)</span>
              </li>
            ) : (
              <li className="feature-item excluded">
                <CrossIcon />
                <span>Alertes de valorisation par email (dès 6 mois)</span>
              </li>
            )}

            {/* Watchlist IA */}
            {activePlan.hasWatchlistIa ? (
              <li className="feature-item included">
                <CheckIcon />
                <span><strong>Watchlist IA</strong> & Analyse globale du marché</span>
              </li>
            ) : (
              <li className="feature-item excluded">
                <CrossIcon />
                <span>Watchlist IA (dès 6 mois)</span>
              </li>
            )}

            {/* BONUS EXCLUSIFS ANNUEL */}
            {activePlan.hasBonus ? (
              <li className="feature-item included" style={{ background: 'rgba(108, 63, 196, 0.08)', padding: '8px 10px', borderRadius: 8 }}>
                <CheckIcon />
                <span style={{ color: '#6C3FC4', fontWeight: 800 }}>
                  🎁 Bonus & rapports d'analyse exclusifs (Inclus avec l'offre annuelle)
                </span>
              </li>
            ) : (
              <li className="feature-item excluded">
                <CrossIcon />
                <span>Bonus exclusifs & rapports (dès l'engagement annuel)</span>
              </li>
            )}

            <li className="feature-item included">
              <CheckIcon />
              <span>Support prioritaire & Paiement Mobile Money certifié</span>
            </li>
          </ul>

          <div className="pricing-card-cta-wrapper">
            <button
              type="button"
              className="pricing-btn-pro"
              onClick={() => navigate('/members/signup')}
            >
              S'inscrire et choisir {activePlan.planName} →
            </button>
            <span className="pricing-card-guarantee">
              Paiement Mobile Money (Wave, Orange, MTN, Moov) ou Carte
            </span>
          </div>
        </div>

      </div>

      {/* ── BANNIÈRE INSTITUTIONNELLE & FORMATEURS (B2B) ── */}
      <div className="pricing-b2b-banner">
        <div className="pricing-b2b-content">
          <div className="pricing-b2b-tag">
            🏢 ENTREPRISES · SGI · INSTITUTIONNELS · FORMATEURS
          </div>
          <h4 className="pricing-b2b-title">
            Vous êtes une SGI, une banque, une société de gestion ou un coach en Bourse ?
          </h4>
          <p className="pricing-b2b-desc">
            Les abonnements ci-dessus sont strictement réservés aux investisseurs particuliers à titre personnel (B2C). 
            Pour une licence d'entreprise, un accès multi-utilisateurs, une intégration API ou un usage de formation/académie, contactez-nous pour une offre B2B sur-mesure.
          </p>
        </div>
        <a 
          href="mailto:contact@kaneabourse.com?subject=Demande%20Offre%20B2B%20/%20Institutionnel%20/%20Formateur"
          className="pricing-b2b-btn"
        >
          Demander une offre B2B →
        </a>
      </div>

      {/* ── MOYENS DE PAIEMENT SÉCURISÉS ── */}
      <PaymentMethods marginTop={36} theme="landing" />
    </section>
  );
};

export default LandingPricingSection;
